import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, Pencil, Copy, Printer, Search } from "lucide-react";
import { toast } from "sonner";

function statusBadge(s) {
  const map = {
    draft: "bg-slate-100 text-slate-600",
    saved: "bg-blue-50 text-blue-700",
    printed: "bg-green-50 text-green-700",
    reprinted: "bg-emerald-50 text-emerald-700",
  };
  return map[s] || "bg-slate-100 text-slate-600";
}

export default function PrescriptionHistory() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  const load = async (search = "") => {
    const { data } = await api.get(`/prescriptions${search ? `?q=${encodeURIComponent(search)}` : ""}`);
    setItems(Array.isArray(data) ? data : []);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q]);

  const dup = async (id) => {
    try {
      const { data } = await api.post(`/prescriptions/${id}/duplicate`);
      toast.success("Duplicated");
      nav(`/prescriptions/${data.id}/edit`);
    } catch { toast.error("Could not duplicate"); }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="history-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>Prescription History</h1>
          <p className="text-slate-500 text-sm mt-1">{items.length} record{items.length === 1 ? "" : "s"}</p>
        </div>
        <Button onClick={() => nav("/prescriptions/new")} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-new-prescription">
          New Prescription
        </Button>
      </div>

      <Card className="p-4 border-slate-200 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by Rx# or patient name..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="rx-search" />
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Rx #</th>
                <th className="px-4 py-3 text-left font-semibold">Patient</th>
                <th className="px-4 py-3 text-left font-semibold">Date</th>
                <th className="px-4 py-3 text-left font-semibold">Meds</th>
                <th className="px-4 py-3 text-left font-semibold">Doctor</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">No prescriptions yet.</td></tr>
              ) : items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`history-row-${r.id}`}>
                  <td className="px-4 py-3 mono text-xs text-slate-700">{r.prescription_number}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{r?.patient_snapshot?.full_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{(r.date || "").slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{(r.medicines || []).length}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{r?.doctor_snapshot?.name || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${statusBadge(r.status)}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button asChild size="sm" variant="ghost"><Link to={`/prescriptions/${r.id}/preview`} data-testid={`history-view-${r.id}`}><Eye size={14} /></Link></Button>
                    <Button asChild size="sm" variant="ghost"><Link to={`/prescriptions/${r.id}/edit`} data-testid={`history-edit-${r.id}`}><Pencil size={14} /></Link></Button>
                    <Button size="sm" variant="ghost" onClick={() => dup(r.id)} data-testid={`history-dup-${r.id}`}><Copy size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => window.open(`/prescriptions/${r.id}/preview?print=1`, "_self")} data-testid={`history-print-${r.id}`}><Printer size={14} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
